import { SafeError, SafePromise, SafeType } from '@benzinga/safe-await';
import { ExtendedListenableSubscribable } from '@benzinga/subscribable';

import { StockSymbol } from '@benzinga/session';
import { egressAddTickerToWatchlist, egressUpdateWatchlistSettings, egressUpdateWatchlistTicker } from './egress';
import { normalizeWatchlistSymbol } from './ingest';
import { Watchlist, WatchlistSymbol } from './entities';
import { WatchlistRestful } from './restful/restful';
import { ingestSymbols, ingestSymbol, ingestWatchlist, ingestCreatedWatchlist } from './ingest';
import { Session } from '@benzinga/session';

interface ErrorEvent {
  error?: SafeError;
  errorType:
    | 'watchlist:get_watchlist_error'
    | 'watchlist:create_watchlist_error'
    | 'watchlist:update_watchlist_settings_error'
    | 'watchlist:update_watchlist_ticker_error'
    | 'watchlist:remove_watchlist_error'
    | 'watchlist:verification_error';
  type: 'error';
}
interface TickerErrorEvent {
  error?: SafeError;
  symbols: StockSymbol[];
  watchlist: Watchlist;
  errorType: 'watchlist:add_ticker_to_watchlist_error' | 'watchlist:remove_watchlist_ticker_error';
  type: 'error';
}

interface WatchlistEvent {
  type: 'watchlist:updating_watchlist_settings' | 'watchlist:removing_watchlist';
  watchlist: Watchlist;
}

interface WatchlistCreateEvent {
  type: 'watchlist:creating_watchlist';
  watchlistName: string;
}

interface WatchlistUpdateEvent {
  type: 'watchlist:retrieving_watchlists';
}

interface WatchlistTickerAddEvent {
  symbols: StockSymbol[];
  type: 'watchlist:adding_ticker_to_watchlist';
  watchlist: Watchlist;
}

interface WatchlistUpdateTickerEvent {
  symbol: WatchlistSymbol;
  type: 'watchlist:updating_ticker_settings';
}

interface WatchlistTickerRemoveEvent {
  symbols: WatchlistSymbol[];
  type: 'watchlist:removing_tickers_from_watchlist';
  watchlist: Watchlist;
}

export type WatchlistRequestEvent =
  | ErrorEvent
  | TickerErrorEvent
  | WatchlistEvent
  | WatchlistCreateEvent
  | WatchlistUpdateEvent
  | WatchlistTickerAddEvent
  | WatchlistUpdateTickerEvent
  | WatchlistTickerRemoveEvent;

interface WatchlistFunctions {
  addTickersToWatchlist: WatchlistRequest['addTickersToWatchlist'];
  createWatchlist: WatchlistRequest['createWatchlist'];
  getWatchlists: WatchlistRequest['getWatchlists'];
  removeTickersFromWatchlist: WatchlistRequest['removeTickersFromWatchlist'];
  removeWatchlist: WatchlistRequest['removeWatchlist'];
  updateWatchlistSettings: WatchlistRequest['updateWatchlistSettings'];
  updateWatchlistTicker: WatchlistRequest['updateWatchlistTicker'];
}

export class WatchlistRequest extends ExtendedListenableSubscribable<WatchlistRequestEvent, WatchlistFunctions> {
  private restful: WatchlistRestful;

  constructor(session: Session) {
    super();
    this.restful = new WatchlistRestful(session);
  }

  public getWatchlists = async (): SafePromise<Watchlist[]> => {
    this.dispatch({
      type: 'watchlist:retrieving_watchlists',
    });
    const watchlists = await ingestWatchlist(this.restful.getWatchlists());
    if (watchlists.err) {
      this.dispatch({
        error: watchlists.err,
        errorType: 'watchlist:get_watchlist_error',
        type: 'error',
      });
    }
    return watchlists;
  };

  public createWatchlist = async (
    watchlistName: string,
    settings: Partial<Exclude<Watchlist, 'watchlistId'>>,
    symbols: WatchlistSymbol[],
  ): SafePromise<Watchlist> => {
    this.dispatch({
      type: 'watchlist:creating_watchlist',
      watchlistName,
    });
    const createdWatchlist = await this.restful.createWatchlist(
      watchlistName,
      egressUpdateWatchlistSettings(settings),
      symbols.map(s => egressUpdateWatchlistTicker(s)),
    );
    if (createdWatchlist.err) {
      this.dispatch({
        error: createdWatchlist.err,
        errorType: 'watchlist:create_watchlist_error',
        type: 'error',
      });
      return createdWatchlist as SafeType<Watchlist>;
    } else {
      const ingestedWatchlist = ingestCreatedWatchlist(createdWatchlist.ok);
      return { ok: ingestedWatchlist };
    }
  };

  public updateWatchlistSettings = async (watchlist: Watchlist): SafePromise<Watchlist> => {
    this.dispatch({
      type: 'watchlist:updating_watchlist_settings',
      watchlist,
    });
    const watchlistAlert = await ingestWatchlist(
      this.restful.updateWatchlistSettings(egressUpdateWatchlistSettings(watchlist)),
    );
    if (watchlistAlert.err) {
      this.dispatch({
        error: watchlistAlert.err,
        errorType: 'watchlist:update_watchlist_settings_error',
        type: 'error',
      });
      return watchlistAlert as SafeType<Watchlist>;
    } else {
      return { ok: watchlistAlert.ok[0] };
    }
  };

  public updateWatchlistTicker = async (watchlistSymbol: WatchlistSymbol): SafePromise<WatchlistSymbol> => {
    this.dispatch({
      symbol: watchlistSymbol,
      type: 'watchlist:updating_ticker_settings',
    });
    const watchlistAlertSymbol = await ingestSymbol(
      this.restful.updateWatchlistTicker(egressUpdateWatchlistTicker(watchlistSymbol)),
    );
    if (watchlistAlertSymbol.err) {
      this.dispatch({
        error: watchlistAlertSymbol.err,
        errorType: 'watchlist:update_watchlist_ticker_error',
        type: 'error',
      });
      return watchlistAlertSymbol as SafeType<WatchlistSymbol>;
    } else {
      return watchlistAlertSymbol as SafeType<WatchlistSymbol>;
    }
  };

  public addTickersToWatchlist = async (
    watchlist: Watchlist,
    symbols: StockSymbol[],
  ): SafePromise<WatchlistSymbol[]> => {
    this.dispatch({
      symbols,
      type: 'watchlist:adding_ticker_to_watchlist',
      watchlist,
    });
    const watchlistSymbol = await ingestSymbols(
      this.restful.addTickersToWatchlist(egressAddTickerToWatchlist(watchlist, symbols)),
    );
    if (watchlistSymbol.err) {
      this.dispatch({
        error: watchlistSymbol.err,
        errorType: 'watchlist:add_ticker_to_watchlist_error',
        symbols,
        type: 'error',
        watchlist,
      });
    } else {
      const symbolsMissed = symbols.filter(
        symbol =>
          !watchlistSymbol.ok.some(
            addedSymbol => normalizeWatchlistSymbol(addedSymbol.symbol) === normalizeWatchlistSymbol(symbol),
          ),
      );
      if (symbolsMissed.length !== 0) {
        this.dispatch({
          error: watchlistSymbol.err,
          errorType: 'watchlist:add_ticker_to_watchlist_error',
          symbols: symbolsMissed,
          type: 'error',
          watchlist,
        });
      }
    }
    return watchlistSymbol;
  };

  public removeTickersFromWatchlist = async (
    watchlist: Watchlist,
    symbols: WatchlistSymbol[],
  ): SafePromise<WatchlistSymbol[]> => {
    this.dispatch({
      symbols,
      type: 'watchlist:removing_tickers_from_watchlist',
      watchlist,
    });
    const promises = symbols.map(symbol => this.restful.removeTickerFromWatchlist(watchlist, symbol));
    const results = await Promise.all(promises);
    const [succeeded, failed] = results.reduce<[WatchlistSymbol[], WatchlistSymbol[]]>(
      (statuses, result, idx) => {
        if (result.err) {
          statuses[1].push(symbols[idx]);
        } else {
          statuses[0].push(symbols[idx]);
        }
        return statuses;
      },
      [[], []],
    );
    if (failed.length !== 0) {
      this.dispatch({
        errorType: 'watchlist:remove_watchlist_ticker_error',
        symbols: failed.map(wlSymbol => wlSymbol.symbol),
        type: 'error',
        watchlist,
      });
    }
    return { ok: succeeded };
  };

  public removeWatchlist = async (watchlist: Watchlist): SafePromise<undefined> => {
    this.dispatch({
      type: 'watchlist:removing_watchlist',
      watchlist,
    });
    const removed = await this.restful.removeWatchlist(watchlist);
    if (removed.err) {
      this.dispatch({
        error: removed.err,
        errorType: 'watchlist:remove_watchlist_error',
        type: 'error',
      });
    }
    return removed;
  };

  public getYodleeAccess = async (): SafePromise<{ data: { accessToken: string } }> => {
    return this.restful.getYodleeAccess();
  };

  public syncYodleeWatchlists = async (): SafePromise<undefined> => {
    return this.restful.syncYodleeWatchlists();
  };

  protected onSubscribe(): WatchlistFunctions {
    return {
      addTickersToWatchlist: this.addTickersToWatchlist,
      createWatchlist: this.createWatchlist,
      getWatchlists: this.getWatchlists,
      removeTickersFromWatchlist: this.removeTickersFromWatchlist,
      removeWatchlist: this.removeWatchlist,
      updateWatchlistSettings: this.updateWatchlistSettings,
      updateWatchlistTicker: this.updateWatchlistTicker,
    };
  }
}
